import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefCallback,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * CSAGE-577: a fast tooltip for labels that the native `title` attribute
 * served badly — the browser shows `title` only after 0.5-1.5 s, and never on
 * keyboard focus. This opens shortly after hover and at once on focus, closes
 * on leave / blur / Escape, renders in a portal (so an `overflow:hidden`
 * parent cannot cut it) and keeps itself inside the window.
 *
 * The trigger stays the caller's own element: `children` receives the props
 * to spread on it. Styles: `.sage-tooltip` in styles/base.css (tokens only).
 */

/** Hover delay before the tooltip opens. Short enough to feel immediate, long
 *  enough that sweeping the pointer down a list does not flash every row. */
export const TOOLTIP_OPEN_DELAY_MS = 150;

/** Gap between the trigger and the tooltip, and the minimum window margin. */
const GAP_PX = 6;
const EDGE_PX = 8;

export interface TooltipTriggerProps {
  ref: RefCallback<HTMLElement>;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
  'aria-describedby'?: string;
}

export interface TooltipProps {
  /** Tooltip text. `null` or empty means no tooltip. */
  content: ReactNode;
  /** Checked on every open; return false to stay closed (e.g. label not cut off). */
  when?: (trigger: HTMLElement) => boolean;
  children: (trigger: TooltipTriggerProps) => ReactElement;
}

type Placement = 'bottom' | 'top';

export function Tooltip({ content, when, children }: TooltipProps): ReactElement {
  const id = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; placement: Placement }>({
    top: 0,
    left: 0,
    placement: 'bottom',
  });

  const hasContent = content !== null && content !== undefined && content !== '';

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const canOpen = useCallback((): boolean => {
    const el = triggerRef.current;
    return hasContent && el !== null && (when ? when(el) : true);
  }, [hasContent, when]);

  const show = useCallback(() => {
    clearTimer();
    if (canOpen()) setOpen(true);
  }, [canOpen, clearTimer]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  // Place the tooltip once it is in the DOM and its size is known: below the
  // trigger, or above it when it would leave the bottom of the window; moved
  // left when it would leave the right edge.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tipRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const below = t.bottom + GAP_PX;
    const placement: Placement =
      below + tip.height + EDGE_PX > window.innerHeight && t.top - GAP_PX - tip.height >= EDGE_PX
        ? 'top'
        : 'bottom';
    const top = placement === 'top' ? t.top - GAP_PX - tip.height : below;
    const maxLeft = window.innerWidth - tip.width - EDGE_PX;
    const left = Math.max(EDGE_PX, Math.min(t.left, maxLeft));
    setPos({ top, left, placement });
  }, [open]);

  const trigger: TooltipTriggerProps = {
    ref: (el) => {
      triggerRef.current = el;
    },
    onPointerEnter: () => {
      clearTimer();
      timer.current = setTimeout(show, TOOLTIP_OPEN_DELAY_MS);
    },
    onPointerLeave: hide,
    onFocus: show,
    onBlur: hide,
    onKeyDown: (e) => {
      if (e.key === 'Escape') hide();
    },
    ...(open ? { 'aria-describedby': id } : {}),
  };

  return (
    <>
      {children(trigger)}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="sage-tooltip"
            data-placement={pos.placement}
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
